import { createSelector } from 'reselect';
import { ActiveLineData } from 'flow/types/ActiveLineData';
import { ChatMember } from 'flow/types/Chats';
import { ChatSessionKey, ChatUserKey } from 'flow/types/EntityKeys';
import {
    MeetingMember as MeetingMemberRecord,
    MeetingNumber,
    MeetingParticipantIdentity,
    VideoConferenceConfig,
} from 'flow/types/Meeting';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { listToMap } from 'flow/utils';
import { getVideoConferenceWithSip } from 'flow/utils/browser-utils';
import {
    getCountryCodeFromNumber,
    prettyFormatE164Number,
} from 'flow/utils/call-utils';
import {
    HARDCODED_CONFERENCE_NUMBER_LIST,
    INTERNATIONAL_CONFERENCE_NUMBER,
    MeetingType,
} from 'flow/utils/meeting-constants';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import { roomTypes } from 'flow/utils/message-utils-TW';
import { checkFailedConnectionState, DisplayMode } from 'flow/utils/rtc-utils';
import { getFocusedLocalCallId } from './call-selectors';
import {
    selectChatName,
    selectChatSessionAdmins,
    selectChatSessionIsDirectMessage,
    selectChatSessionIsRoom,
    selectChatSessionRoomType,
    selectMemberInChat,
} from './chat-selectors';
import { selectOwnExtensionKey } from './ExtensionSelectors';
import { selectOwnChatUserKey } from './own-extension-selectors';
import { selectVideoChatConnected } from './video-chat-selectors';
import {
    selectOwnWaitingRoomStanding,
    selectVideoConnectionState,
} from './video-selectors';

/** Active meeting */

export const selectActiveMeetingCallId = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'call', 'callId']);

export const selectActiveMeetingCallConnectionState = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'call', 'callConnectionState']);

export const selectActiveMeetingCallSession = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'call', 'callSession']);

export const selectActiveMeetingCallSessionStartTime = (state: State) =>
    selectActiveMeetingCallSession(state)?.start_time;

export const selectActiveMeetingCallUserAgent = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'call', 'userAgent']);

export const selectActiveMeetingAudioReference = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'call', 'audioReference']);

export const selectConferenceMeetingCallingMembers = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'callingMembers']);

export const selectActiveMeetingChatOpen = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'chatOpen']);

export const selectActiveMeetingChatSessionId = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'chatSessionId']);

export const selectActiveMeetingChatSessionKey = (state: State) =>
    'chatsession-'.concat(
        selectActiveMeetingChatSessionId(state),
    ) as ChatSessionKey;

export const selectActiveMeetingMeetingAccess = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'meetingAccess']);

// Whether the viewer is an admin of the active meeting's chat-service room
// (see MeetingParticipantsList).
export const selectActiveMeetingViewerIsRoomAdmin = (state: State) =>
    selectViewerIsChatSessionAdmin(
        state,
        selectActiveMeetingChatSessionKey(state),
    );

// The backend's own admin verdict (VID-264, set via registerParticipantIdentity),
// fetched alongside the rest of the active meeting's data. Kept as an OR
// alongside the chat-session-admins-list check above rather than a
// replacement, in case the two ever disagree while the room's chat-service
// membership data lags behind.
export const selectActiveMeetingViewerIsMeetingAdmin = (state: State) =>
    !!state.meeting.getIn(['activeMeeting', 'viewerIsMeetingAdmin']);

// Same check as selectActiveMeetingViewerIsRoomAdmin, keyed directly by
// chatSessionKey — for a room lobby, which has no active meeting yet.
export const selectViewerIsChatSessionAdmin = (
    state: State,
    chatSessionKey?: ChatSessionKey,
) => {
    const chatSessionAdmins = selectChatSessionAdmins(state, chatSessionKey);
    const ownChatUserKey = selectOwnChatUserKey(state);
    return !!ownChatUserKey && chatSessionAdmins.includes(ownChatUserKey);
};

/**
 * Whether someone has successfully created and linked a persistent
 * chat-service room to this meeting. Deliberately independent of
 * chatSessionId: some meetings (e.g. instant meetings) already have a
 * chatSessionId from creation, before anyone has explicitly activated
 * history — so the "Activate chat history" affordance can't key off session
 * existence alone.
 */
export const selectActiveMeetingChatHistoryActivated = (
    state: State,
): boolean =>
    state.meeting.getIn(['activeMeeting', 'chatHistoryActivated'], false);

/**
 * Every participant in an active meeting has chat access: chat-service members
 * see the merged panel ({@code VideoChatSession}), everyone else falls through
 * to the ephemeral video-chat relay ({@code VideoChatEphemeral}). Kept as a
 * named selector for the call sites it already powers — and so future
 * meeting-state gating has one place to live.
 */
export const selectActiveMeetingHasAccessToChat = (_state: State) => true;

/**
 * Whether the panel that *this* user would open is unusable because the relay
 * isn't connected. Users on the merged panel chat via chat-service and are
 * never blocked by relay health; users on the ephemeral panel (guests, and
 * internal users in meetings with no chat-service session — e.g. "My meeting"
 * or an ad-hoc 1:1) have no other channel, so we disable the chat entry points
 * for them while disconnected.
 */
export const selectEphemeralChatUnavailable = (state: State) => {
    const isGuest = !selectOwnExtensionKey(state);
    const chatSessionId = selectActiveMeetingChatSessionId(state);
    const usesEphemeralPanel =
        isGuest || !selectMemberInChat(state, chatSessionId);
    return usesEphemeralPanel && !selectVideoChatConnected(state);
};

export const selectActiveMeetingChatName = (state: State) => {
    const chatSessionId = selectActiveMeetingChatSessionId(state);
    return selectChatName(state, chatSessionId);
};

/**
 * Instant and room-for-later meetings always get a chatSessionId at
 * creation, so its absence means this meeting was never created through
 * either flow — e.g. the personal meeting room, reached via
 * /messages/conferences.
 */
export const selectActiveMeetingHasLinkedChatSession = (state: State) =>
    !!selectActiveMeetingChatSessionId(state);

/**
 * True when the meeting's linked chat session is a real, pre-existing 1:1
 * direct message (not a session created for/by this meeting). Such sessions
 * already have persistent history, so there is nothing to "activate" — and
 * activating would incorrectly rewrite the DM's roomType to a meeting room.
 */
export const selectActiveMeetingChatSessionIsDirectMessage = (state: State) => {
    const chatSessionId = selectActiveMeetingChatSessionId(state);
    return selectChatSessionIsDirectMessage(state, chatSessionId);
};

export const selectActiveMeetingChatSessionIsRoom = (state: State) => {
    const chatSessionId = selectActiveMeetingChatSessionId(state);
    return selectChatSessionIsRoom(state, chatSessionId);
};

// True once activated, or if the room already has more than just its creator as a member (AC-1685's signal — roomType is 'room' from creation for instant meetings too).
export const selectActiveMeetingHasPersistentChatHistory = (
    state: State,
): boolean =>
    selectActiveMeetingChatHistoryActivated(state) ||
    selectActiveMeetingChatRoomHasMultipleMembers(state);

// Who may regenerate the meeting's link/PIN (AC-1691): nobody during an ongoing call; otherwise everyone except in a room, where only admins can. Fails closed to admin-only if the session hasn't loaded yet. Takes the chatSessionId of the session actually being displayed — the caller must not assume that's the active meeting (e.g. the lobby and instant-meeting modal render before any meeting is active).
export const selectViewerCanRegenerateLink = (
    state: State,
    chatSessionId?: number | string | null,
) => {
    if (selectMeetingIsActive(state)) {
        return false;
    }
    // `undefined` is "chatSessionId itself hasn't loaded yet" (the lobby slice
    // has no default for it), distinct from a loaded, genuinely-absent
    // `null`/`0` — chatSessionId can arrive before the rest of the fetch, so
    // treating "not yet known" as "no session" would let a non-admin member
    // regenerate during that window. Fail closed until it's known either way.
    if (chatSessionId === undefined) {
        return false;
    }
    // No chat session at all — a bare conference (e.g. a personal "My
    // meeting"), which has no room and no chat-service admin concept to
    // fail closed to. Its one owner may always regenerate.
    if (!chatSessionId) {
        return true;
    }
    const sessionLoaded = selectMemberInChat(state, chatSessionId);
    const chatSessionKey = 'chatsession-'.concat(
        chatSessionId,
    ) as ChatSessionKey;
    return sessionLoaded && !selectChatSessionIsRoom(state, chatSessionId)
        ? true
        : selectViewerIsChatSessionAdmin(state, chatSessionKey);
};

export const selectActiveMeetingViewerCanRegenerateLink = (state: State) =>
    selectViewerCanRegenerateLink(
        state,
        selectActiveMeetingChatSessionId(state),
    );

export const selectLobbyViewerCanRegenerateLink = (state: State) => {
    const chatSessionId = selectLobbyChatSessionId(state);
    const canRegenerate = selectViewerCanRegenerateLink(state, chatSessionId);
    // selectViewerCanRegenerateLink hands a bare conference's "no chat
    // session" branch to anyone, trusting the caller to only be asking on
    // the owner's behalf — true for a settings page, not for the lobby: any
    // signed-in colleague holding the owner's shared join link lands here
    // too (VideoRoutes -> fetchLobbyData falls back to the ?room= param), so
    // that branch needs an explicit ownership check the shared selector has
    // no way to make on its own.
    return canRegenerate && !chatSessionId
        ? !!selectLobbyViewerIsMeetingHost(state)
        : canRegenerate;
};

export const selectActiveMeetingIsHovering = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'isHovering']);

export const selectActiveMeetingIsNarrowWindow = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'isNarrowWindow']);

export const selectActiveMeetingIsOnHold = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'isOnHold']);

export const selectActiveMeetingCode = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'meetingCode']);

/**
 * Whether this viewer is in the meeting, as opposed to on their way into one.
 * joinMeeting sets the active meeting code before the room is joined, so the
 * code alone does not answer it: someone held in the waiting room (VID-76) has
 * a meeting code and is still outside the door.
 *
 * Anything that renders the meeting has to ask this rather than the code, or it
 * hands the meeting to someone still waiting — with controls over a room they
 * cannot see or be seen in. MeetingMain and CommunicationCanvas each decided
 * this for themselves and drifted apart on exactly that, which is why the rule
 * lives here now.
 *
 * Being admitted is the *absence* of a standing: roomJoined clears it to null,
 * so null covers both "never knocked" and "knocked and was let in". Hence "no
 * standing at all" rather than "not WAITING" — which also keeps DENIED out,
 * and it has to, since a denial does not clear the meeting code (the rejected
 * join is swallowed by startMeeting, so the teardown never runs).
 *
 * That makes this fail closed, which is the right default for a door: a state
 * added later — VID-417's BLOCK outcomes, say — keeps the viewer out until
 * someone decides otherwise here. The cost is the opposite case: an explicit
 * ADMITTED state, should anyone ever add one, would read as "outside" and must
 * be handled here rather than assumed.
 */
export const selectViewerIsInMeeting = (state: State) =>
    !!selectActiveMeetingCode(state) && !selectOwnWaitingRoomStanding(state);

/**
 * Whether this join went in expecting to be held — the viewer asked to join, so
 * an awaitingApproval is the answer to that question. When it is false and
 * signaling holds us anyway, home has told the client one thing and signaling
 * another, and the wait is nobody's intent (VID-76).
 */
export const selectJoinExpectedToBeHeld = (state: State) =>
    !!state.meeting.getIn(['activeMeeting', 'joinExpectedToBeHeld']);

export const selectActiveMeetingView = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'meetingView']);

export const selectActiveMeetingViewIsFullscreen = (state: State) =>
    selectActiveMeetingView(state) === DisplayMode.FULLSCREEN.key;

export const selectActiveMeetingViewIsMinimized = (state: State) =>
    selectActiveMeetingView(state) === DisplayMode.MINIMIZED.key;

export const selectActiveMeetingViewIsThumbnail = (state: State) =>
    selectActiveMeetingView(state) === DisplayMode.THUMBNAIL.key;

export const selectActiveMeetingMembers = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'members']);

export const selectActiveMeetingHasOtherParticipants = (state: State) => {
    const members = selectActiveMeetingMembers(state);
    return members ? members.size > 1 : false;
};

// Reads only the active meeting's own chat-room member list, not the whole
// chats.sessions map — an unrelated session's activity (a message, someone
// typing, another room updating) doesn't change this reference, so it isn't
// a dependency of selectActiveMeetingChatRoomMemberChatUserKeys below.
//
// Returns undefined both when there's no linked chat session and when the
// session simply hasn't been loaded into state.chats yet (e.g. a viewer who
// joined the meeting but never opened chat) — deliberately the same "unknown"
// signal in both cases, distinct from a session that's loaded but genuinely
// has no members. selectMemberInChat (chat-selectors.ts) uses the same
// session-presence check as the app's definition of "loaded".
const selectActiveMeetingChatRoomMembersList = (state: State) => {
    const chatSessionId = selectActiveMeetingChatSessionId(state);
    if (!chatSessionId) return undefined;
    const session = state.chats?.getIn([
        'sessions',
        `chatsession-${chatSessionId}` as ChatSessionKey,
    ]);
    if (!session) return undefined;
    return session.get('members');
};

/**
 * undefined means "we don't know this room's membership" (chat session not
 * loaded for this viewer) — distinct from an empty Set, which means "loaded,
 * and genuinely has no members". Callers must treat undefined as "don't
 * guess" rather than "empty", or every participant reads as a guest for any
 * viewer who hasn't opened chat (see deriveMeetingMemberGuestInfo).
 */
export const selectActiveMeetingChatRoomMemberChatUserKeys = createSelector(
    [selectActiveMeetingChatRoomMembersList],
    (members): Set<string> | undefined => {
        if (!members) return undefined;
        return new Set(
            members
                .map((m: TypedMap<ChatMember>) => m.get('key'))
                .filter(Boolean)
                .toArray(),
        );
    },
);

export type MeetingMemberGuestBadge = 'internal' | 'external' | null;

/**
 * Shared by useMeetingMembers.ts (the participants panel) and
 * makeSelectMeetingMemberGuestBadge (video tiles) so the guest / internal
 * derivation lives in exactly one place. A participant is a guest once this
 * meeting has a chat room at all and they either have no chatUserKey
 * (external, or internal but not yet chat-provisioned) or their chatUserKey
 * isn't a room member.
 *
 * `identity` is the server-computed classification (VID-264, home PR #13771):
 * `external`/`chatUserKey` captured at join and stamped on each
 * ConferenceMemberDTO, replacing the old client-side heuristic that matched
 * a resolved contact against the extensions list — which silently
 * misclassified any colleague who joined with no phone number attached (a
 * numberless web participant resolves to no contact at all, indistinguishable
 * from a true external guest under the old contact/extension match).
 *
 * Two "unknown" cases are deliberately treated as "not a guest" rather than
 * guessed as external/guest, since mislabeling a colleague as external in
 * front of the whole meeting is the worse error direction than under-labeling:
 *
 * - No `identity` at all: happens whenever VID-264 hasn't rolled out
 *   everywhere yet (client-server #2234 / home #13771), or briefly on join
 *   before the fire-and-forget registration call (joinMeeting) has landed and
 *   the first roster poll has run. Absence of data isn't evidence of being
 *   external.
 * - `chatRoomMemberKeys` is undefined: the viewer hasn't loaded this chat
 *   session (e.g. joined via link and never opened chat), so we don't know
 *   who's in the room — not the same as the room having no members.
 */
export const deriveMeetingMemberGuestInfo = (
    identity: TypedMap<MeetingParticipantIdentity> | undefined,
    chatSessionId: string | number | null | undefined,
    chatRoomMemberKeys: Set<string> | undefined,
): {
    isGuest: boolean;
    isInternal: boolean;
    chatUserKey: ChatUserKey | undefined;
} => {
    if (!identity) {
        return { isGuest: false, isInternal: false, chatUserKey: undefined };
    }
    const chatUserKey = identity.get('chatUserKey');
    const isInternal = identity.get('external') === false;
    const isGuest =
        !!chatSessionId &&
        !!chatRoomMemberKeys &&
        (!chatUserKey || !chatRoomMemberKeys.has(chatUserKey));

    return { isGuest, isInternal, chatUserKey };
};

export const selectActiveMeetingMeetingType = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'meetingType']);

/**
 * True when this meeting was created as an instant meeting, per the
 * meetingType the backend persisted at creation (AC-1493). The API
 * serialises the enum value lowercase ('instant'), while MeetingType.key
 * here is uppercase ('INSTANT'), so compare case-insensitively.
 */
export const selectActiveMeetingIsInstantMeeting = (state: State) => {
    const meetingType = selectActiveMeetingMeetingType(state);
    return (
        typeof meetingType === 'string' &&
        meetingType.toUpperCase() === MeetingType.INSTANT.key
    );
};

/**
 * Whether a participant (identified by their video-signaling member name,
 * which is the same displayName carried on the active meeting's roster
 * members) is a guest — live in the call but not a permanent chat room
 * member — and if so, whether they're a colleague (internal) or not
 * (external). Parameterised (see the makeSelectContactDataForNumber pattern)
 * so each video tile gets its own memoized instance. Excludes self: the local
 * user always renders through its own local tile, never TileVideoParticipant/
 * TileDialInParticipant, but a creator who isn't yet a room member would
 * otherwise read as a guest here too.
 */
export const makeSelectMeetingMemberGuestBadge = () =>
    createSelector(
        [
            selectActiveMeetingMembers,
            selectSenderName,
            selectActiveMeetingChatSessionId,
            selectActiveMeetingChatRoomMemberChatUserKeys,
            (_state: State, memberName: string | undefined) => memberName,
        ],
        (
            members,
            ownName,
            chatSessionId,
            chatRoomMemberKeys,
            memberName,
        ): MeetingMemberGuestBadge => {
            if (!memberName || memberName === ownName) return null;
            const member = members?.find(
                (m: TypedMap<MeetingMemberRecord>) =>
                    m.get('displayName') === memberName,
            );
            if (!member) return null;
            const { isGuest, isInternal } = deriveMeetingMemberGuestInfo(
                member.get('identity'),
                chatSessionId,
                chatRoomMemberKeys,
            );
            if (!isGuest) return null;
            return isInternal ? 'internal' : 'external';
        },
    );

// Whether an ephemeral-relay message's sender is actually an internal colleague — the relay's own isExternal flag is about message routing, not identity, so this uses the same identity source the video tile badge does.
export const makeSelectExternalMessageSenderIsInternal = () =>
    createSelector(
        [
            selectActiveMeetingMembers,
            selectActiveMeetingChatSessionId,
            selectActiveMeetingChatRoomMemberChatUserKeys,
            (_state: State, senderName: string | undefined) => senderName,
        ],
        (members, chatSessionId, chatRoomMemberKeys, senderName): boolean => {
            if (!members || !senderName) return false;
            const normalizedSenderName = sliceUUIDFromUserName(senderName);
            const member = members.find(
                (m: TypedMap<MeetingMemberRecord>) =>
                    sliceUUIDFromUserName(m.get('displayName')) ===
                    normalizedSenderName,
            );
            if (!member) return false;
            return deriveMeetingMemberGuestInfo(
                member.get('identity'),
                chatSessionId,
                chatRoomMemberKeys,
            ).isInternal;
        },
    );

export const selectActiveMeetingMemberListActiveStatus = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'membersListActive']);

export const selectActiveMeetingJoinInfoActive = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'joinInfoActive']);

export const selectActiveMeetingHostControlsActive = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'hostControlsActive']);

export const selectRightPanelActive = (state: State) => {
    const chatIsOpen = selectActiveMeetingChatOpen(state);
    const memberListIsOpen = selectActiveMeetingMemberListActiveStatus(state);
    const joinInfoIsOpen = selectActiveMeetingJoinInfoActive(state);
    const hostControlsIsOpen = !!selectActiveMeetingHostControlsActive(state);
    return (
        chatIsOpen || memberListIsOpen || joinInfoIsOpen || hostControlsIsOpen
    );
};

export type MeetingRightPanelKind =
    | 'chat'
    | 'participants'
    | 'joinInfo'
    | 'hostControls'
    | null;

// The 4 right-panel flags are mutually exclusive by construction (every
// open/toggle thunk that sets one closes the other 3 — see meeting-actions.js),
// so at most one of these is ever true. Collapsing them into a single "which
// panel, if any" value lets the panel slot (MeetingRightPanel) render exactly
// one MeetingSidePanel and just swap its content, instead of mounting 4
// independent sliding panels that would each animate on every switch.
export const selectActiveMeetingRightPanelKind = createSelector(
    [
        selectActiveMeetingChatOpen,
        selectActiveMeetingMemberListActiveStatus,
        selectActiveMeetingJoinInfoActive,
        selectActiveMeetingHostControlsActive,
    ],
    (
        chatIsOpen,
        memberListIsOpen,
        joinInfoIsOpen,
        hostControlsIsOpen,
    ): MeetingRightPanelKind => {
        if (chatIsOpen) return 'chat';
        if (memberListIsOpen) return 'participants';
        if (joinInfoIsOpen) return 'joinInfo';
        if (hostControlsIsOpen) return 'hostControls';
        return null;
    },
);

export const selectActiveMeetingMonitorInterval = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'monitorInterval']);

export const selectActiveMeetingNumber = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'number']);

export const selectActiveMeetingPin = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'pin']);

export const selectActiveMeetingPollingInterval = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'pollingInterval']);

export const selectActiveMeetingReconnectActive = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'reconnectActive']);

export const selectActiveMeetingReconnectAttempt = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'reconnectAttempt']);

export const selectActiveMeetingScreenShareMemberName = (state: State) =>
    state.meeting.getIn(['activeMeeting', 'screenShareMemberName']);

/** Error */
export const selectVideoError = (state: State) => state.meeting.get('error');

export const selectVideoErrorText = (state: State) =>
    state.meeting.getIn(['error', 'text']);

export const selectVideoErrorSpinner = (state: State) =>
    state.meeting.getIn(['error', 'spinner']);

export const selectVideoErrorShow = (state: State) =>
    state.meeting.getIn(['error', 'show']);

export const selectVideoErrorReconnectOnClick = (state: State) =>
    state.meeting.getIn(['error', 'reconnectOnClick']);

/** Devices */
export const selectMicAllowed = (state: State) =>
    state.meeting.getIn(['devices', 'micAllowed']);

export const selectVideoAllowed = (state: State) =>
    state.meeting.getIn(['devices', 'videoAllowed']);

export const selectMicInUse = (state: State) =>
    state.meeting.getIn(['devices', 'inUse', 'mic']);

export const selectAudioOutputInUse = (state: State) =>
    state.meeting.getIn(['devices', 'inUse', 'audioOutput']);

export const selectVideoInUse = (state: State) =>
    state.meeting.getIn(['devices', 'inUse', 'video']);

export const selectMicDeviceList = (state: State) =>
    state.meeting.getIn(['devices', 'deviceLists', 'mics']);

export const selectAudioOutputDeviceList = (state: State) =>
    state.meeting.getIn(['devices', 'deviceLists', 'audioOutputs']);

export const selectVideoDeviceList = (state: State) =>
    state.meeting.getIn(['devices', 'deviceLists', 'videos']);

/** Lobby */
export const selectLobbyMembers = (state: State) =>
    state.meeting.getIn(['lobby', 'members']);

export const selectLobbyPin = (state: State) =>
    state.meeting.getIn(['lobby', 'pin']);

export const selectLobbyNumber = (state: State) =>
    state.meeting.getIn(['lobby', 'number']);

export const selectLobbyMeetingCode = (state: State) =>
    state.meeting.getIn(['lobby', 'meetingCode']);

export const selectLobbyChatSessionId = (state: State) =>
    state.meeting.getIn(['lobby', 'chatSessionId']);

export const selectLobbyChatSessionIsRoom = (state: State) =>
    selectChatSessionIsRoom(state, selectLobbyChatSessionId(state));

export const selectLobbyChatSessionKey = (state: State) => {
    const chatSessionId = selectLobbyChatSessionId(state);
    return chatSessionId
        ? ('chatsession-'.concat(chatSessionId) as ChatSessionKey)
        : undefined;
};

export const selectLobbyVideoStream = (state: State) =>
    state.meeting.getIn(['lobby', 'videoStream']);

export const selectLobbyAudioStream = (state: State) =>
    state.meeting.getIn(['lobby', 'audioStream']);

export const selectLobbyAudioActive = (state: State) =>
    state.meeting.getIn(['lobby', 'audioActive']);

export const selectLobbyVideoActive = (state: State) =>
    state.meeting.getIn(['lobby', 'videoActive']);

export const selectLobbyAudioDeviceInUse = (state: State) =>
    state.meeting.getIn(['lobby', 'deviceInUse', 'audioOutput']);

export const selectLobbyMicDeviceInUse = (state: State) =>
    state.meeting.getIn(['lobby', 'deviceInUse', 'mic']);

export const selectLobbyVideoDeviceInUse = (state: State) =>
    state.meeting.getIn(['lobby', 'deviceInUse', 'video']);

export const selectLobbyCameraError = (state: State) =>
    state.meeting.getIn(['lobby', 'cameraError']);

export const selectLobbyRoomNotJoinedRoomName = (state: State) =>
    state.meeting.getIn(['lobby', 'roomNotJoined', 'roomName']);

export const selectLobbyRoomNotJoinedCause = (state: State) =>
    state.meeting.getIn(['lobby', 'roomNotJoined', 'cause']);

export const selectLobbyMeetingAccess = (state: State) =>
    state.meeting.getIn(['lobby', 'meetingAccess']);

export const selectLobbyRequireHostPresent = (state: State) =>
    state.meeting.getIn(['lobby', 'requireHostPresent']);

export const selectLobbyMeetingJoinBlockedReason = (state: State) =>
    state.meeting.getIn(['lobby', 'meetingJoinBlockedReason']);

export const selectLobbyViewerIsMeetingHost = (state: State) =>
    state.meeting.getIn(['lobby', 'viewerIsMeetingHost']);

export const selectLobbyHostPresent = (state: State): boolean | null =>
    state.meeting.get('lobby').get('hostPresent');

/**
 * The "prevent joining before host" gate (VID-198), composed in one place so the
 * join button, the lobby message and the join action all answer the same
 * question: the meeting requires a host, none has started it, and this viewer
 * is not one of them.
 *
 * Closes only on an explicit `false` from home. Home reports hostPresent on a
 * live fetch and fails it open whenever it cannot tell, so a value that is
 * missing altogether (an older backend, a non-live fetch, a failed load) must
 * read the same way here rather than trap everyone behind a gate nobody can
 * open.
 */
export const selectLobbyHostGateClosed = (state: State): boolean =>
    selectLobbyRequireHostPresent(state) === true &&
    selectLobbyHostPresent(state) === false &&
    !selectLobbyViewerIsMeetingHost(state);

/** Logs */
export const selectNumberOfMissedLogs = (state: State) =>
    state.meeting.getIn(['logs', 'numberOfMissedLogs']);

export const selectNoNetworkLogs = (state: State) =>
    state.meeting.getIn(['logs', 'logsList']);

const selectApiMeetingNumbers = (state: State) =>
    state.meeting.get('meetingNumbers');

/** Sender */
export const selectSenderName = (state: State) =>
    state.meeting.getIn(['sender', 'name']);

// Whether any live, non-self internal participant isn't a room member yet — the real "something to activate" signal (AC-1685), since roomType is 'room' from creation.
export const selectActiveMeetingHasUninvitedInternalGuest = createSelector(
    [
        selectActiveMeetingMembers,
        selectSenderName,
        selectActiveMeetingChatSessionId,
        selectActiveMeetingChatRoomMemberChatUserKeys,
    ],
    (members, ownName, chatSessionId, chatRoomMemberKeys): boolean => {
        if (!members) return false;
        return members.some((member: TypedMap<MeetingMemberRecord>) => {
            if (member.get('displayName') === ownName) return false;
            const { isGuest, isInternal } = deriveMeetingMemberGuestInfo(
                member.get('identity'),
                chatSessionId,
                chatRoomMemberKeys,
            );
            return isGuest && isInternal;
        });
    },
);

// Whether the room already has more than just its creator as a member — the persisted, refresh-safe "a group chat already exists" signal (AC-1685), since there's no dedicated activation flag on the backend.
export const selectActiveMeetingChatRoomHasMultipleMembers = createSelector(
    [selectActiveMeetingChatRoomMemberChatUserKeys],
    (chatRoomMemberKeys): boolean =>
        !!chatRoomMemberKeys && chatRoomMemberKeys.size > 1,
);

/**
 * Whether the viewer themselves is an internal colleague of the meeting's
 * host company, per the same server-computed identity (VID-264) used for
 * other members' guest badges. Used to gate host controls that must stay
 * internal-only (e.g. removing another participant, VID-311/VID-57) — an
 * external guest shouldn't get that capability, even though it's fine for
 * them to be its target. Same "unknown identity => not internal" convention
 * as deriveMeetingMemberGuestInfo, for the same reason: under-labeling is the
 * safer error here.
 */
export const selectViewerIsInternal = createSelector(
    [selectActiveMeetingMembers, selectSenderName],
    (members, ownName): boolean => {
        const ownMember = members?.find(
            (m: TypedMap<MeetingMemberRecord>) =>
                m.get('displayName') === ownName,
        );
        const identity = ownMember?.get('identity');
        return identity ? identity.get('external') === false : false;
    },
);

/**
 * Whether the viewer may remove another participant (VID-311, AC-1804).
 * Always internal-only. By context:
 * - Room: admins only.
 * - Bare/personal conference (no chat session, e.g. "My meeting"): owner only.
 * - Any other chat session (1:1, ad-hoc multi-party, other non-room link):
 *   every internal participant — no owner/admin concept applies.
 *
 * Branches on the session's roomType rather than on whether a chats.sessions
 * entry merely exists: a session entry can appear in the store (e.g. from an
 * unrelated incoming message) before its roomType has actually been fetched,
 * so entry-presence alone can't tell "confirmed not a room" from "don't know
 * yet". The home/chat-service GET session response always includes roomType
 * once it returns at all (access is all-or-nothing at the request level, not
 * filtered per field by membership) — so roomType being unknown is only ever
 * a brief in-flight-fetch window, never a lasting state. Fails closed during
 * that window instead of granting remove in what might be a room (AC-1804
 * follow-up).
 */
export const selectViewerCanRemoveParticipants = createSelector(
    [
        selectViewerIsInternal,
        selectActiveMeetingChatSessionId,
        selectActiveMeetingViewerIsRoomAdmin,
        selectActiveMeetingViewerIsMeetingAdmin,
        (state: State) => {
            const chatSessionId = selectActiveMeetingChatSessionId(state);
            if (!chatSessionId) {
                return undefined;
            }
            const chatSessionKey = 'chatsession-'.concat(
                chatSessionId,
            ) as ChatSessionKey;
            return selectChatSessionRoomType(state, chatSessionKey);
        },
    ],
    (
        viewerIsInternal,
        chatSessionId,
        isRoomAdmin,
        isMeetingAdmin,
        roomType,
    ): boolean => {
        if (!viewerIsInternal) {
            return false;
        }
        if (!chatSessionId) {
            return isMeetingAdmin;
        }
        if (roomType === undefined) {
            return false;
        }
        if (roomType === roomTypes.room) {
            return isRoomAdmin || isMeetingAdmin;
        }
        return true;
    },
);

// Gates the Host Controls panel's footer entry point (VID-53): only the
// meeting host. "Host" is either admin signal — the chat-service room's
// admins list, or the backend's own per-viewer verdict (see
// selectActiveMeetingViewerIsMeetingAdmin).
export const selectShouldShowHostControlsPanel = createSelector(
    [
        selectActiveMeetingViewerIsRoomAdmin,
        selectActiveMeetingViewerIsMeetingAdmin,
    ],
    (isRoomAdmin, isMeetingAdmin): boolean => isRoomAdmin || isMeetingAdmin,
);

export const selectSenderAudioActive = (state: State) =>
    state.meeting.getIn(['sender', 'audioActive']);

export const selectSenderAudioStream = (state: State) =>
    state.meeting.getIn(['sender', 'audioStream']);

/** Others */
export const selectIsUserLoggedIn = (state: State) =>
    !!selectOwnExtensionKey(state);

// Whether the "Activate chat history" button/prompt should show at all —
// only relevant for instant meetings, since a room-for-later/scheduled
// meeting's chat already has history. Shared by ActivateChatHistoryButton
// and the guest-notice banner (VideoChatSession), which needs to know this
// to decide whether to show its "Activate history to keep this conversation"
// follow-up line alongside the button.
export const selectShouldShowActivateChatHistoryButton = createSelector(
    [
        selectIsUserLoggedIn,
        selectActiveMeetingViewerIsRoomAdmin,
        selectActiveMeetingHasOtherParticipants,
        selectActiveMeetingChatHistoryActivated,
        selectActiveMeetingChatRoomHasMultipleMembers,
        selectActiveMeetingHasUninvitedInternalGuest,
        selectActiveMeetingIsInstantMeeting,
        selectActiveMeetingChatSessionIsDirectMessage,
        selectActiveMeetingHasLinkedChatSession,
    ],
    (
        isLoggedIn,
        isHost,
        hasOtherParticipants,
        chatHistoryActivated,
        hasActivatedGroupChat,
        hasUninvitedInternalGuest,
        isInstantMeeting,
        isExistingDirectMessage,
        hasLinkedChatSession,
    ): boolean =>
        isLoggedIn &&
        isHost &&
        hasOtherParticipants &&
        !chatHistoryActivated &&
        !hasActivatedGroupChat &&
        hasUninvitedInternalGuest &&
        isInstantMeeting &&
        !isExistingDirectMessage &&
        hasLinkedChatSession,
);

// TODO: Replace this function once call-selectors.js is Typescripted
const _getIsPhoneConnected = (state: State) => state.phone.get('isConnected');

export const selectShouldUseExistingUserAgent = createSelector(
    [selectIsUserLoggedIn, _getIsPhoneConnected],
    (userIsLoggedIn, phoneIsConnected) => {
        const handleMeetingAsPhoneCall = getVideoConferenceWithSip();
        return userIsLoggedIn && phoneIsConnected && handleMeetingAsPhoneCall;
    },
);

export const selectActiveMeetingMembersFirstNames = createSelector(
    [selectActiveMeetingMembers, selectSenderName],
    (members, senderName) => {
        const ownName = sliceUUIDFromUserName(senderName);
        const membersName = members
            .sortBy((member) => member.get('joinTime'))
            .map((member) => sliceUUIDFromUserName(member.get('displayName')));

        const membersFirstNames = [];
        for (let i = 0; i < membersName.size; i++) {
            const name = membersName.get(i);
            if (name === ownName) {
                continue;
            }
            const firstName = name.split(' ')[0];
            membersFirstNames.push(firstName);
        }
        return membersFirstNames;
    },
);

export const selectMeetingIsInFocus = (state: State) => {
    const focusedCallId = getFocusedLocalCallId(state);
    const meetingId = selectActiveMeetingCode(state);
    return focusedCallId && focusedCallId === meetingId;
};

export const selectIsSameLobbyAsActiveMeeting = (state: State) => {
    const lobbyMeetingCode = selectLobbyMeetingCode(state);
    const activeMeetingCode = selectActiveMeetingCode(state);
    return (
        lobbyMeetingCode !== null &&
        activeMeetingCode !== null &&
        lobbyMeetingCode === activeMeetingCode
    );
};

export const selectMeetingIsActive = (state: State) =>
    !!selectActiveMeetingCode(state);

export const selectHasNoRtcConnection = (state: State) => {
    const videoConnectionState = selectVideoConnectionState(state);
    const callConnectionState = selectActiveMeetingCallConnectionState(state);
    return (
        checkFailedConnectionState(callConnectionState) ||
        checkFailedConnectionState(videoConnectionState)
    );
};

export const selectNumberOfUnreadMessages = (state: State) =>
    state.chats.getIn(
        [
            'sessions',
            selectActiveMeetingChatSessionKey(state),
            'numberOfUnreadMessages',
        ],
        0,
    );

export const selectFullscreenOrNarrowWindow = (state: State) =>
    selectActiveMeetingViewIsFullscreen(state) ||
    selectActiveMeetingIsNarrowWindow(state);

/** Conference numbers */
export const selectMeetingNumbers = createSelector(
    [selectApiMeetingNumbers],
    (apiMeetingNumbers) => {
        let allMeetingNumbers: MeetingNumber[] =
            HARDCODED_CONFERENCE_NUMBER_LIST;
        if (apiMeetingNumbers?.length > 0) {
            allMeetingNumbers =
                HARDCODED_CONFERENCE_NUMBER_LIST.concat(apiMeetingNumbers);
        }
        return allMeetingNumbers;
    },
);

export const getConferenceNumber = (
    state: State,
    ownActiveCall: TypedMap<ActiveLineData>,
    ownCountryCode: string,
) => {
    if (!ownActiveCall?.has('conference')) {
        return null;
    }

    const sourceNumber = ownActiveCall.get('source');
    const countryCode = getCountryCodeFromNumber(sourceNumber);
    const meetingNumbers = selectMeetingNumbers(state);
    const country = meetingNumbers?.find(
        (country) => country.countryCode === countryCode?.toLowerCase(),
    );
    const prettyNumber =
        country &&
        prettyFormatE164Number(country.conferenceNumber, ownCountryCode);

    return prettyNumber || INTERNATIONAL_CONFERENCE_NUMBER;
};

/** Video conference config */
export const selectVideoConferenceConfig = (
    state: State,
): VideoConferenceConfig | null =>
    state.meeting.get('videoConferenceConfig')?.toJS() ?? null;

export const selectAllMeetingNumbersSorted = createSelector(
    [selectMeetingNumbers],
    (meetingNumbers) => {
        const numberMap = listToMap(
            meetingNumbers,
            (e: MeetingNumber) => e.countryCode,
        );

        return new Map<string, MeetingNumber>(
            [...numberMap].sort(([, a], [, b]) =>
                a.countryNameInEnglish.localeCompare(b.countryNameInEnglish),
            ),
        );
    },
);
